import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  Divider,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Chip,
} from "@mui/material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { NotificationEvents, NotificationEventLabels } from "../../constants/notificationEvents";
import { updateUser } from "../../services/userService";

const parseBool = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const EVENT_DETAILS = {
  [NotificationEvents.USER_FORGOT_PASSWORD]: "Alert when your account password reset is requested.",
  [NotificationEvents.USER_PASSWORD_CHANGED]: "Alert when your account password is changed.",
  [NotificationEvents.USER_ASSIGNED_NEW_LOCATION]: "Alert when your access is updated with new customer/site allocations.",
  [NotificationEvents.USER_REQUESTLIST_PURCHASABLE]: "Alert when request-list parts become purchasable.",
  [NotificationEvents.USER_NEW_DOCUMENT_ADDED]: "Alert when new documents are published for your allowed sites.",
  [NotificationEvents.USER_DOCUMENT_MODIFIED]: "Alert when existing documents are updated for your allowed sites.",
  [NotificationEvents.USER_ORDER_SUCCESS]: "Alert when your order is successfully submitted.",
  [NotificationEvents.USER_PART_ORDER_REMINDER]: "Reminder to complete pending part orders.",
  [NotificationEvents.ADMIN_NEW_USER_REGISTERED]: "Alert when a new user account is registered.",
  [NotificationEvents.ADMIN_NEW_ORDER_PLACED]: "Alert when a new customer order is placed.",
  [NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE]: "Alert when a customer-care/request escalation is raised.",
  [NotificationEvents.ADMIN_NEW_CASE_CREATED]: "Alert when a new support case is created.",
  [NotificationEvents.ADMIN_CONTACT_CCR_FORM]: "Alert when a Contact CCR form is submitted.",
  [NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY]: "Alert when a machine-sale notification is submitted.",
  [NotificationEvents.USER_STEP_SYNC_REPORT]: "Nightly STEP/CAD onboarding summary. Turn Email off to stop receiving it.",
};

const PREFERENCE_GROUPS = [
  {
    id: "account_security",
    title: "Account and Security",
    description: "Personal account and login related alerts.",
    events: [
      NotificationEvents.USER_FORGOT_PASSWORD,
      NotificationEvents.USER_PASSWORD_CHANGED,
      NotificationEvents.USER_ASSIGNED_NEW_LOCATION,
    ],
  },
  {
    id: "orders_requests",
    title: "Orders and Request List",
    description: "Order lifecycle and request-list updates.",
    events: [
      NotificationEvents.USER_ORDER_SUCCESS,
      NotificationEvents.USER_PART_ORDER_REMINDER,
      NotificationEvents.USER_REQUESTLIST_PURCHASABLE,
      NotificationEvents.ADMIN_NEW_ORDER_PLACED,
      NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE,
    ],
  },
  {
    id: "cases_support",
    title: "Cases and Customer Care",
    description: "Support-case and CCR submission alerts.",
    events: [
      NotificationEvents.ADMIN_NEW_CASE_CREATED,
      NotificationEvents.ADMIN_CONTACT_CCR_FORM,
    ],
  },
  {
    id: "platform_updates",
    title: "Platform and Content",
    description: "Operational and content-level notifications.",
    events: [
      NotificationEvents.USER_NEW_DOCUMENT_ADDED,
      NotificationEvents.USER_DOCUMENT_MODIFIED,
      NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY,
      NotificationEvents.USER_STEP_SYNC_REPORT,
    ],
  },
  {
    id: "user_admin",
    title: "User Administration",
    description: "User-management oversight notifications.",
    events: [NotificationEvents.ADMIN_NEW_USER_REGISTERED],
  },
];

/**
 * Decide whether a single notification toggle should be shown to the current user.
 *
 * Hides anything the user could never actually receive, so the screen only ever lists
 * real choices — an admin-only event for a non-admin, or an event whose feature is
 * switched off globally, would otherwise render a switch that does nothing.
 *
 * @param {object} args
 * @param {string} args.eventKey One of `NotificationEvents`.
 * @param {boolean} args.isAdmin Whether the viewer holds an Admin-type role.
 * @param {(path: string) => boolean} args.hasPermission Permission probe from `useAuth`.
 * @param {boolean} args.notifyOrderRequest User meta flag gating the order/request admin events.
 * @param {boolean} args.notifyCaseCreation User meta flag gating the case/CCR admin events.
 * @param {boolean} args.machineSellNotifyEnabled Global option gating the machine-sale event.
 * @param {boolean} args.receivesStepSyncReport Whether this user's role is one of the roles
 *   currently selected to receive the STEP sync report. The report is addressed by role in
 *   SuperAdmin > Additional Settings, so for everyone else the opt-out would be a switch with
 *   no effect — worse than absent, because turning it off would imply they were on the list.
 * @returns {boolean} True when the toggle should render.
 */
const isEventVisible = ({ eventKey, isAdmin, hasPermission, notifyOrderRequest, notifyCaseCreation, machineSellNotifyEnabled, receivesStepSyncReport }) => {
  const adminEvents = new Set([
    NotificationEvents.ADMIN_NEW_USER_REGISTERED,
    NotificationEvents.ADMIN_NEW_ORDER_PLACED,
    NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE,
    NotificationEvents.ADMIN_NEW_CASE_CREATED,
    NotificationEvents.ADMIN_CONTACT_CCR_FORM,
    NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY,
  ]);
  if (adminEvents.has(eventKey) && !isAdmin) return false;

  switch (eventKey) {
    case NotificationEvents.ADMIN_NEW_USER_REGISTERED:
      return (
        hasPermission("user_management.can_view_pending_users_for_approval") &&
        (
          hasPermission("user_management.create_users") ||
          hasPermission("user_management.edit_users") ||
          hasPermission("user_management.delete_users")
        )
      );
    case NotificationEvents.ADMIN_NEW_ORDER_PLACED:
      return notifyOrderRequest && (hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order"));
    case NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE:
      return notifyOrderRequest && (hasPermission("requests_management.view_request_history") || hasPermission("requests_management.allow_notify_customer_care"));
    case NotificationEvents.ADMIN_NEW_CASE_CREATED:
      return notifyCaseCreation && (hasPermission("service_management.view_cases") || hasPermission("service_management.create_cases"));
    case NotificationEvents.ADMIN_CONTACT_CCR_FORM:
      return notifyCaseCreation && (hasPermission("requests_management.view_request_history") || hasPermission("requests_management.allow_notify_customer_care"));
    case NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY:
      return machineSellNotifyEnabled && (hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order"));
    case NotificationEvents.USER_REQUESTLIST_PURCHASABLE:
      return hasPermission("requests_management.view_request_history");
    case NotificationEvents.USER_ORDER_SUCCESS:
    case NotificationEvents.USER_PART_ORDER_REMINDER:
      return hasPermission("order_management.view_order_history") || hasPermission("order_management.create_order");
    case NotificationEvents.USER_STEP_SYNC_REPORT:
      return receivesStepSyncReport;
    default:
      return true;
  }
};

const NOTIFICATION_HELP_TEXT = {
  [NotificationEvents.ADMIN_NEW_USER_REGISTERED]: 'Sent to admins when a new user registers.',
  [NotificationEvents.ADMIN_NEW_ORDER_PLACED]: 'Sent to admins when a customer places an order.',
  [NotificationEvents.ADMIN_NOTIFY_CUSTOMER_CARE]: 'Sent to admins when a user notifies Customer Care.',
  [NotificationEvents.ADMIN_NEW_CASE_CREATED]: 'Sent to admins when a new support case is created.',
  [NotificationEvents.ADMIN_CONTACT_CCR_FORM]: 'Sent to admins when a Contact CCR form is submitted.',
  [NotificationEvents.ADMIN_MACHINE_SELL_NOTIFY]: 'Sent to admins when a machine sale is submitted.',
  //  [NotificationEvents.ADMIN_NEW_DOCUMENT_ADDED]: 'Sent to admins when a new document is added to the system.',
  // [NotificationEvents.ADMIN_DOCUMENT_MODIFIED]: 'Sent to admins when an existing document is modified.',

  // Pipeline (Super Admin / System Level)
  [NotificationEvents.PIPELINE_COMPLETED]: 'Sent when Daily Document sync is completed successfully.',
  [NotificationEvents.PIPELINE_INTERRUPTED]: 'Sent when Daily Document sync is interrupted or fails.',


  [NotificationEvents.USER_FORGOT_PASSWORD]: 'Sent when a password reset is requested for your account.',
  [NotificationEvents.USER_PASSWORD_CHANGED]: 'Sent when your password is changed.',
  [NotificationEvents.USER_ASSIGNED_NEW_LOCATION]: 'Sent when a new customer location is assigned to you.',
  [NotificationEvents.USER_REQUESTLIST_PURCHASABLE]: 'Sent when a request list item becomes purchasable.',
  [NotificationEvents.USER_NEW_DOCUMENT_ADDED]: 'Sent when a new document is added to your accessible sites.',
  [NotificationEvents.USER_DOCUMENT_MODIFIED]: 'Sent when an existing document is modified in your accessible sites.',
  [NotificationEvents.USER_ORDER_SUCCESS]: 'Sent when your order is placed successfully.',
  [NotificationEvents.USER_PART_ORDER_REMINDER]: 'Reminder to complete a pending parts order.',
  [NotificationEvents.USER_STEP_SYNC_REPORT]: 'Nightly report of STEP/CAD models onboarded and failed.',

};

export default function NotificationPreferences() {
  const { user, updateUserStatus, hasPermission } = useAuth();
  const { optionKeys } = useOptions();
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  const isAdmin = String(user?.role?.role_type || user?.role?.role_name || user?.user_role || "").trim().toLowerCase() === "admin";
  const isViewer = [user?.role?.role_name, user?.role?.role_type, user?.user_role]
    .filter(Boolean)
    .map((v) => String(v).trim().toLowerCase())
    .includes("viewer");
  const notifyOrderRequest = parseBool(user?.user_meta?.notify_order_creations_and_requests, false);
  const notifyCaseCreation = parseBool(user?.user_meta?.notify_new_case_creation_notifications, false);
  const machineSellNotifyEnabled = parseBool(optionKeys?.enableMachineSellNotifyForm, false);

  // The STEP sync report is addressed by ROLE (options.stepSyncReportSuccessRoles, a
  // comma-separated list of role ids set in SuperAdmin > Additional Settings), so the
  // opt-out only means anything to someone whose role is on that list. Split on the same
  // /[,;\s]+/ the server's resolveRoleRecipientEmails uses, so the two never disagree about
  // what counts as a separator. user_id 1 is excluded there as well, so the switch would be
  // inert for the super admin and is hidden rather than shown doing nothing.
  const receivesStepSyncReport = useMemo(() => {
    if (Number(user?.user_id) === 1) return false;
    const selectedRoleIds = String(optionKeys?.stepSyncReportSuccessRoles || "")
      .split(/[,;\s]+/)
      .map((value) => Number(String(value).trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!selectedRoleIds.length) return false;
    const ownRoleId = Number(user?.role?.role_id ?? user?.user_role);
    return Number.isFinite(ownRoleId) && selectedRoleIds.includes(ownRoleId);
  }, [optionKeys?.stepSyncReportSuccessRoles, user?.user_id, user?.role?.role_id, user?.user_role]);

  const visibleGroups = useMemo(() => {
    const groups = PREFERENCE_GROUPS.map((group) => {
      const visibleEvents = group.events.filter((eventKey) =>
        isEventVisible({ eventKey, isAdmin, hasPermission, notifyOrderRequest, notifyCaseCreation, machineSellNotifyEnabled, receivesStepSyncReport })
      );
      return { ...group, visibleEvents };
    }).filter((group) => group.visibleEvents.length > 0 && !(isViewer && group.id === 'orders_requests'));
    return groups;
  }, [hasPermission, isAdmin, isViewer, notifyOrderRequest, notifyCaseCreation, machineSellNotifyEnabled, receivesStepSyncReport]);

  const visibleEventKeys = useMemo(
    () => visibleGroups.flatMap((group) => group.visibleEvents),
    [visibleGroups]
  );

  useEffect(() => {
    const existing = user?.user_meta?.notification_preferences;
    let parsed = {};
    try {
      parsed = typeof existing === "string" ? JSON.parse(existing) : existing || {};
    } catch (_err) {
      parsed = {};
    }
    const defaults = {};
    Object.values(NotificationEvents).forEach((eventKey) => {
      defaults[eventKey] = { email: true, inapp: true };
    });
    [NotificationEvents.USER_NEW_DOCUMENT_ADDED, NotificationEvents.USER_DOCUMENT_MODIFIED].forEach((eventKey) => {
      defaults[eventKey] = { email: false, inapp: false };
    });
    setPrefs({ ...defaults, ...parsed });
  }, [user]);

  const toggle = (key, channel) => (e) => {
    const checked = e.target.checked;
    setPrefs((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [channel]: checked },
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      const filteredPrefs = {};
      visibleEventKeys.forEach((eventKey) => {
        filteredPrefs[eventKey] = {
          email: !!prefs?.[eventKey]?.email,
          inapp: !!prefs?.[eventKey]?.inapp,
        };
      });

      const reqBody = {
        userId: user.user_id,
        mode: "profile",
        data: {
          user_first_name: user.user_first_name,
          user_last_name: user.user_last_name,
          user_email: user.user_email,
          user_role: user.user_role,
          user_display_name: user.user_display_name,
          user_phone: user.user_phone,
          user_meta: {
            notification_preferences: JSON.stringify(filteredPrefs),
          },
        },
      };
      const res = await updateUser(reqBody);
      if (!res?.status) throw new Error("Save failed");
      updateUserStatus({
        user_meta: {
          ...(user?.user_meta || {}),
          notification_preferences: filteredPrefs,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: t("UI_TEXT.NOTIFICATION_PREFERENCES_UPDATED"),
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to save preferences.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Stack spacing={0.25} sx={{ mb: 1.25 }}>
        <Typography variant="h6">{t("UI_TEXT.NOTIFICATIONS_1")}</Typography>
        <Typography variant="body2" color="text.secondary">
          Choose how you want to receive each notification. Only relevant options for your role and access are shown.
        </Typography>
      </Stack>

      {/* {isAdmin && !notifyOrderRequest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Order and request escalation admin notifications are hidden because "Receive notifications for order creations and request list" is disabled for this user.
        </Alert>
      )}
      {isAdmin && !notifyCaseCreation && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Case creation and Contact CCR admin notifications are hidden because "Receive notifications for new case creation and Contact CCR form submissions" is disabled for this user.
        </Alert>
      )} */}

      <Stack spacing={2}>
        {visibleGroups.map((group) => (
          <Box
            key={group.id}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1.1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.3 }}
            >
              <Stack spacing={0.3} justifyContent="center">
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {group.title}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>
                  {group.description}
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={`${group.visibleEvents.length} options`}
                sx={{ fontSize: 11 }}
              />
            </Stack>
            <Divider sx={{ my: "8px" }} />
            <Grid container spacing={1.25}>
              {group.visibleEvents.map((eventKey) => (
                <Grid item xs={12} md={6} key={eventKey}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      backgroundColor: "#fff",
                      px: 1.25,
                      py: 0.9,
                      height: "100%",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack spacing={0.35} justifyContent="center">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            {NotificationEventLabels[eventKey] || eventKey}
                          </Typography>
                          {String(eventKey || "").startsWith("ADMIN_") && (
                            <Chip
                              size="small"
                              label="Admin only"
                              sx={{ height: 18, fontSize: 10, fontWeight: 500 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>
                          {EVENT_DETAILS[eventKey] || "Notification preference"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        {eventKey !== NotificationEvents.USER_ORDER_SUCCESS && !(isViewer && eventKey === NotificationEvents.USER_PART_ORDER_REMINDER) && (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={!!prefs?.[eventKey]?.email}
                                onChange={toggle(eventKey, "email")}
                              />
                            }
                            label={<Typography sx={{ fontSize: 12 }}>Email</Typography>}
                          />
                        )}
                        {/* The STEP sync report exists only as a nightly email, so an in-app
                            switch for it would never change anything. */}
                        {eventKey !== NotificationEvents.USER_STEP_SYNC_REPORT && (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={!!prefs?.[eventKey]?.inapp}
                                onChange={toggle(eventKey, "inapp")}
                              />
                            }
                            label={<Typography sx={{ fontSize: 12 }}>In-app</Typography>}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
        <Button variant="outlined" onClick={() => window.location.reload()} disabled={saving}>
          {t("BUTTON.CANCEL")}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save Preferences
        </Button>
      </Box>
    </Box>
  );
}
